import { Component, OnInit, Input } from '@angular/core';
import {
  DashboardFeedItemJobApplication,
  JobApplication,
  JobApplicationAssessments,
  JobDetailSnapshot,
} from '@bluecrew/crew-member-backend-angular-client';
import { StepProgress } from 'src/app/common/application-progress/application-progress.component';
import { some } from 'lodash-es';
import { NavController, ModalController } from '@ionic/angular';
import { JobBriefModalComponent, ModalJobInfo } from 'src/app/common/job-brief-modal/job-brief-modal.component';
import { ActiveDays } from 'src/app/common/working-days-of-week/working-days-of-week.component';
import { AppRoutingUtil } from 'src/app/util/app-routing-util';
import { AnalyticsPlugin } from '@plugins/analytics/analytics-plugin';
import { UserService } from 'src/app/services/user.service';
import { i9AssessmentId, inactiveI9AssessmentId, isAnyI9Assessment, reI9AssessmentId } from 'src/app/util/assessment-util';

@Component({
  selector: 'app-job-application',
  templateUrl: './job-application.component.html',
  styleUrls: ['./job-application.component.scss'],
})
export class JobApplicationComponent implements OnInit {

  progress: StepProgress;
  position: string;
  workplace: string;
  externalId: string;
  snapshot: JobDetailSnapshot;

  @Input()
  get feedItem(): DashboardFeedItemJobApplication { return this._feedItem; }
  set feedItem(feedItem: DashboardFeedItemJobApplication) {
    this._feedItem = feedItem;
    const jobApp = feedItem.jobApplication;

    //todo(CREW-2208): pull the logic for counting completed & total assessments below and the one in application-home.page.ts to a single place
    const containsReOrInactiveI9 = (jobApp) => {
      return some(jobApp.assessments, (a) => [reI9AssessmentId, inactiveI9AssessmentId].includes(a?.assessment.id));
    };

    let assessments = jobApp.assessments;
    if (!jobApp.isEligibleAtApplicationCreation) {
      if (containsReOrInactiveI9(jobApp) || this.userService.isCurrentUserEligible()) {
        assessments = assessments.filter(assessment => !assessment.wasDonePriorToApplication);
      }
    }
    let total = containsReOrInactiveI9(jobApp) ? 0 : 1,
      complete = 0;
    for (let a of assessments) {
      total += 1;
      complete += isCompletedCheck(a, jobApp) ? 1 : 0;
    }

    // Account for employment-info assessment
    if (!containsReOrInactiveI9(jobApp) && (this.userService.isCurrentUserEligible() || this.userService.isCurrentUserPendingWorkAuth())) {
      complete += 1;
    }

    this.progress = {
      total,
      complete,
      isPendingReview: some(jobApp.assessments, (a) => a?.response?.state === 'pendingGrade') && jobApp.passedBackgroundCheck,
      isPendingI9Section2: some(jobApp.assessments, (a) => isAnyI9Assessment(a) && a?.response?.state === 'pendingGrade'),
      isPendingBackgroundCheck:
        some(
          jobApp.assessments,
          (a) => [i9AssessmentId, inactiveI9AssessmentId].includes(a?.assessment.id) && a?.response?.state === 'done'
        ) && !jobApp.passedBackgroundCheck,
    };
    if (jobApp.isEligibleAtApplicationCreation) {
      const bcAssessments = jobApp.assessments.filter((a) => a.assessment.associations?.workplace === '*' && ![i9AssessmentId, inactiveI9AssessmentId].includes(a.assessment.id));
      this.progress.total -= bcAssessments.length + 1;
      this.progress.complete -= bcAssessments.length + 1;
    }
    this.position = jobApp.positionBrief.positionName;
    this.workplace = jobApp.positionBrief.workplaceName;
    this.externalId = jobApp.latestJobId || jobApp.originalJobId;
    this.snapshot = jobApp.latestJobDetailSnapshot || jobApp.originalJobDetailSnapshot;
  }
  private _feedItem: DashboardFeedItemJobApplication;

  constructor(private navCtrl: NavController, private modalCtrl: ModalController, private userService: UserService) {}

  ngOnInit() {}

  async onJobShowMore() {
    AnalyticsPlugin.logEvent({
      name: 'dashboard_application_jobshowmore',
      params: {
        jobId: this.feedItem.jobApplication.latestJobId || this.feedItem.jobApplication.originalJobId,
      },
    });
    const days: ActiveDays = {};
    this.snapshot.workAssignment.daysOfWeek.forEach((d) => (days[d] = true));

    const job: ModalJobInfo = {
      title: this.snapshot.positionName,
      companyName: this.snapshot.workplaceName,
      smallLogo: this.snapshot.workplaceLogoUrl,
      days,
      startTimestamp: this.snapshot.workAssignment.startDateTime,
      endTimestamp: this.snapshot.workAssignment.endDateTime,
      addressLine1: `${this.snapshot.addressDetails?.street}`,
      addressLine2: `${this.snapshot.addressDetails?.city}, ${this.snapshot.addressDetails?.state} ${this.snapshot.addressDetails?.zipcode}`,
      dressCode: this.snapshot.dressCode,
      lat: this.snapshot.addressDetails.latitude,
      lng: this.snapshot.addressDetails.longitude,
      externalId: this.externalId,
      blueshifts: this.snapshot.blueshifts && this.snapshot.blueshifts.length > 1 ? this.snapshot.blueshifts : undefined,
    };
    const modal = await this.modalCtrl.create({
      component: JobBriefModalComponent,
      cssClass: 'bottom-sheet-modal',
      componentProps: { job, caller: 'JobApp' },
    });
    await modal.present();
  }

  async onOpenJobApp() {
    AnalyticsPlugin.logEvent({
      name: 'dashboard_open_application',
      params: {
        jobId: this.feedItem.jobApplication.latestJobId || this.feedItem.jobApplication.originalJobId,
      },
    });
    await this.navCtrl.navigateForward(AppRoutingUtil.APPLICATION_HOME);
  }
}

const isCompletedCheck = (item: JobApplicationAssessments, jobApp: JobApplication) => {
  if (isAnyI9Assessment(item)) {
    return item?.response?.state === 'done' && jobApp.passedBackgroundCheck;
  }
  return item?.response?.state !== 'draft';
};

